import { useState, useContext } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as MuiButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import apiCall from "../../../../utils/apiCall";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NotificationContext } from "../../../../providers/notification";

interface DeactivateApplicant {
  ApplicantId: number;
  FirstName: string;
  LastName: string;
  ApplicationDate: string;
  Gender: string;
  MaritalStatus: string;
  Email: string;
}

interface ComponentProps {
  handleClose: () => void;
  DeactivateApplicantDetails: DeactivateApplicant;
}

const DeactivateApplicantByEmail: React.FC<ComponentProps> = ({ handleClose, DeactivateApplicantDetails }) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { showNotification } = useContext(NotificationContext);

  const handleOpenDialog = () => setConfirmDialogOpen(true);
  const handleCloseDialog = () => setConfirmDialogOpen(false);

  const postDeactivateApplicant = async ({ email }: { email: string }) => {
    return apiCall().delete(`/Applicant/DeleteApplicantByEmail`, {
      params: { email },
    });
  };

  const mutation = useMutation(postDeactivateApplicant, {
    onSuccess: () => {
      showNotification({ message: "Applicant deactivated successfully", type: "success" });
      queryClient.refetchQueries(["getDeactivateApplicants"]);
      handleCloseDialog();
      handleClose();
    },
    onError: (error: any) => {
      showNotification({
        message: error?.response?.data?.title || error.message,
        type: "error",
      });
    },
  });

  const handleConfirmDeactivate = () => {
    mutation.mutate({ email: DeactivateApplicantDetails?.Email });
  };

  return (
    <>
      <Container>
        <h2>Applicant Details</h2>
        <Detail><strong>Name:</strong> {DeactivateApplicantDetails?.FirstName} {DeactivateApplicantDetails?.LastName}</Detail>
        <Detail><strong>Email:</strong> {DeactivateApplicantDetails?.Email}</Detail>
        <Detail><strong>Gender:</strong> {DeactivateApplicantDetails?.Gender}</Detail>
        <Detail><strong>Marital Status:</strong> {DeactivateApplicantDetails?.MaritalStatus}</Detail>
        <Detail><strong>Application Date:</strong> {DeactivateApplicantDetails?.ApplicationDate}</Detail>

        <Actions>
          <StyledButton onClick={handleOpenDialog} color="error" variant="contained">
            Delete Applicant
          </StyledButton>
          <StyledButton onClick={handleClose} variant="outlined">
            Cancel
          </StyledButton>
        </Actions>
      </Container>

      <Dialog open={confirmDialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Are you sure you want to delete this applicant?
            <div style={{ color: "red", fontWeight: "bold", marginTop: "0.5rem" }}>
              This action cannot be undone!
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleCloseDialog} color="primary">
            No
          </MuiButton>
          <MuiButton
            onClick={handleConfirmDeactivate}
            color="error"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? "Processing..." : "Yes, Delete Applicant"}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeactivateApplicantByEmail;

// Styled Components
const Container = styled("div")`
  margin: 2rem;
`;

const Detail = styled("p")`
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const Actions = styled("div")`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const StyledButton = styled(MuiButton)`
  padding: 0.6rem 1.5rem;
  font-weight: bold;
`;
